"use client";
import React, { useEffect, useRef, useState } from "react";
import TitleWithDash from "@/components/TitleWithDash";
import { Formik } from "formik";
import InputField from "@/components/InputField";
import StyledButton from "@/components/StyledButton";
import SelectInput from "@/components/SelectInput";
import axios from "axios";
import TurnstileRecaptcha from "./TurnstileRecaptcha";
import * as Yup from "yup";
import devValue from "@/utils/devValue";

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email address").required("Required"),
  phone: Yup.string()
    .matches(/^[0-9]+$/, "Phone number is not valid")
    .required("Required"),
  clientStatus: Yup.string().required("Required"),
  message: Yup.string().required("Required"),
});

const Form = ({
  fields,
  title,
  button,
  slice = null,
  buttonVariant = "Primary",
  teamMember,
  noMobilePadding = false,
}) => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [runDataLayer, setRunDataLayer] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaValid, setRecaptchaValid] = useState(false);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    if (window === undefined) return;
    if (success || error) {
      setTimeout(() => {
        setSuccess(false);
        setError(false);
      }, 5000);
    }
  }, [success, error]);

  useEffect(() => {
    if (window === undefined) return;
    if (!runDataLayer) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "ContactForm",
    });
    setRunDataLayer(false);
  }, [runDataLayer]);

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setRecaptchaValid(true);
  };

  const handleRecaptchaError = () => {
    setRecaptchaValid(false);
  };

  return (
    <div
      className={`bg-[#fff] ${teamMember ? "" : noMobilePadding ? "p-[0px] md:py-[30px] md:px-[40px]" : "p-[20px] md:py-[30px] md:px-[40px]"} `}
    >
      <div className="mb-[20px] md:mb-[30px]">
        <TitleWithDash
          dashColor={"#C5C5C5"}
          title={
            <h3
              className={`title-large title-font ${teamMember ? "text-[#1F2223] " : "text-[#646464]"}`}
            >
              {title}
            </h3>
          }
        />
      </div>
      <div>
        <Formik
          validationSchema={validationSchema}
          initialValues={{
            firstName: devValue("John"),
            lastName: devValue("Smith"),
            phone: devValue("9876543212"),
            email: devValue("mahesh@atdigital.io"),
            clientStatus: devValue("No"),
            message: devValue("Test Message"),
          }}
          onSubmit={async (values, actions) => {
            const fields = [
              {
                key: "First Name",
                value: values?.firstName,
              },
              {
                key: "Last Name",
                value: values?.lastName,
              },
              {
                key: "Phone Number",
                value: values?.phone,
              },
              {
                key: "Email",
                value: values?.email,
              },
              {
                key: "Are you a new client?",
                value: values?.clientStatus,
              },
              {
                key: "Message",
                value: values?.message,
              },
            ];
            await axios
              .post("/api/send-mail", {
                fields,
                name: values?.firstName + " " + values?.lastName,
                email: values?.email,
                recaptcha_token: recaptchaToken,
              })
              .then((res) => {
                if (!res?.data?.success) return setError(true);
                setSuccess(true);
                if (recaptchaRef.current) {
                  recaptchaRef.current.reset();
                }
                setRecaptchaToken("");
                setRecaptchaValid(false);
                setRunDataLayer(true);
                actions.resetForm();
              });
          }}
        >
          {({ handleSubmit, isSubmitting, errors, touched }) => (
            <form onSubmit={handleSubmit}>
              <div className="flex justify-between items-start gap-x-[15px] mb-[10px]">
                <InputField
                  label={fields?.contact_form_first_name_label}
                  name="firstName"
                />
                <InputField
                  label={fields?.contact_form_last_name_label}
                  name="lastName"
                />
              </div>
              <div className="flex justify-between items-start gap-x-[15px] mb-[10px]">
                <InputField
                  label={fields?.contact_form_email_label}
                  name="email"
                  type="email"
                />
                <InputField
                  label={fields?.contact_form_phone_label}
                  name="phone"
                  type="tel"
                />
              </div>
              <div className="mb-[10px]">
                <SelectInput
                  label={fields?.contact_form_new_client_label}
                  name="clientStatus"
                />
              </div>
              <div className="mb-[24px]">
                <InputField
                  label={fields?.contact_form_message_label}
                  name="message"
                  type="textarea"
                />
              </div>
              <div className="mb-[24px]">
                <TurnstileRecaptcha
                  ref={recaptchaRef}
                  onChange={handleRecaptchaChange}
                  onError={handleRecaptchaError}
                />
              </div>
              {success && (
                <p className="text-base text-[green] pb-[24px]">
                  {fields?.contact_form_success_message}
                </p>
              )}
              {error && (
                <p className="text-base text-red-500 pb-[24px]">
                  {fields?.error_message}
                </p>
              )}
              <div className="flex items-start justify-center">
                {slice && slice?.primary?.submit_button?.length > 0 ? (
                  <>
                    {slice.primary.submit_button.map((item, idx) => (
                      <StyledButton
                        key={`submit_button_${idx}`}
                        disabled={!recaptchaValid || isSubmitting}
                        label={item.label || button}
                        variant={item.variant || buttonVariant}
                        type="submit"
                      />
                    ))}
                  </>
                ) : (
                  <StyledButton
                    disabled={!recaptchaValid || isSubmitting}
                    label={button}
                    variant={buttonVariant}
                    type="submit"
                  />
                )}
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default Form;
